import { useNavigate, useLocation } from 'react-router-dom';
import { useEffect, useMemo, useState } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent } from '@/components/ui/card';
import { Separator } from '@/components/ui/separator';
import { Badge } from '@/components/ui/badge';
import ProductInfoModal from '@/components/ProductInfoModal';
import { useApp } from '@/context/AppContext';
import { ShoppingCart, Package, CheckCircle, ChevronRight, Loader2, AlertCircle, Share2, Calendar, FileText } from 'lucide-react';
import { useBundleSelection, getBundleInfo } from '@/hooks/useBundleSelection';
import { useSnapshot } from '@/hooks/useSnapshot';
import { 
  QuoteData, 
  hasValidSnapshot, 
  getQuoteVersion,
  isV1Quote,
  isV2Quote,
  getCompatibleUserData,
  convertV2ToV1Compatible,
  decomposeKitBundle
} from '@/services/quoteService';
import { handleShopifyCheckout, BundleInfoForCart } from '@/utils/shopifyCheckout';
import ExpiredQuoteBanner from '@/components/ExpiredQuoteBanner';

// Props interface for CarritoStep
interface CarritoStepProps {
  quote?: QuoteData;
}

export default function CarritoStep({ quote }: CarritoStepProps = {}) {
  const navigate = useNavigate();
  const location = useLocation();
  const { state, updateEleccionProgress, updateDispositivo } = useApp();

  // Product detail modal state
  const [productModalOpen, setProductModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<any>(null);
  const [productModalType, setProductModalType] = useState<'equipo' | 'mascarilla' | 'plan'>('equipo');

  // Determine if we're in quote mode and check expiration status
  const isQuoteMode = !!quote;
  const isExpired = quote?.status === 'expired';
  
  // Version detection for quote handling
  const quoteVersion = quote ? getQuoteVersion(quote.userData) : 1;
  const isV1 = !quote || isV1Quote(quote.userData);
  const isV2 = quote && isV2Quote(quote.userData);
  
  // Log version detection for monitoring
  if (quote) {
    console.log(`🔍 CarritoStep quote version: ${quoteVersion} (stored: ${quote.version || 'undefined'})`);
  }
  
  // Determine the page title based on context
  const pageTitle = useMemo(() => {
    // If in quote mode, always show "Cotización"
    if (isQuoteMode) {
      return 'Cotización';
    }
    
    // Check if user came from the selection flow by looking at previous navigation
    // If they navigated directly to /eleccion/carrito via URL, show "Cotización"
    // If they came from the flow, show "Tu carrito"
    const referrer = document.referrer;
    const isDirectAccess = !referrer || !referrer.includes(window.location.origin + '/eleccion/');
    
    return isDirectAccess ? 'Cotización' : 'Tu carrito';
  }, [isQuoteMode]);
  
  // Extract data from quote or app state (version-aware)
  const compatibleUserData = quote ? getCompatibleUserData(quote.userData) : null;
  const equipoSeleccionado = compatibleUserData?.equipoSeleccionado || state.eleccion.equipoSeleccionado;
  const mascarillaSeleccionada = compatibleUserData?.mascarillaSeleccionada || state.eleccion.mascarillaSeleccionada;
  const bunoIncluido = compatibleUserData?.bunoIncluido ?? state.eleccion.bunoIncluido;
  const maskSizing = compatibleUserData?.maskSizing || state.eleccion.maskSizing;

  
  // Extract bundle info for v2 quotes or computed bundle for v1
  const storedBundleInfo = isV2 ? (quote?.userData as any)?.bundleInfo : compatibleUserData?.bundleInfo;
  
  // Use bundle selection hook for both V1 and V2 quotes to get proper bundle images
  // V2 quotes need bundle analysis for image generation, V1 quotes need it for pricing
  const shouldFetchBundle = !isQuoteMode || isV2 || !storedBundleInfo;
  const { bundle: shopifyBundle, fallbackBundle, isLoading: bundleLoading, error: bundleError } = useBundleSelection(
    shouldFetchBundle ? equipoSeleccionado : null,
    shouldFetchBundle ? mascarillaSeleccionada : null
  );
  
  // Snapshot functionality
  const { updateStage, shareSnapshotUrl, markAsUsed, currentSnapshot, isLoading: snapshotLoading, error: snapshotError } = useSnapshot();

  // Handle header back button clicks
  useEffect(() => {
    const handleHeaderBack = () => {
      navigate('/eleccion/productos');
    };

    window.addEventListener('headerBackClick', handleHeaderBack);
    return () => window.removeEventListener('headerBackClick', handleHeaderBack);
  }, [navigate]);

  // Get bundle info based on quote version
  const bundleInfo = useMemo(() => {
    if (isV2 && storedBundleInfo) {
      // V2 quotes: enhance stored bundle with proper image generation
      console.log('📦 Using stored bundle info from v2 quote');
      const enhancedBundle = {
        ...storedBundleInfo,
        // Use V1's bundle image logic for V2 quotes
        image: storedBundleInfo.image || 
               (shopifyBundle?.images?.[0]?.url || 
                fallbackBundle?.image || 
                `https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/${storedBundleInfo.bundleMapping?.shopifyHandle || storedBundleInfo.id}.png`)
      };
      return enhancedBundle;
    } else if (isV1 || shouldFetchBundle) {
      // V1 quotes or live mode: compute bundle from individual selections
      console.log('🔧 Computing bundle info from individual selections');
      return getBundleInfo(equipoSeleccionado, mascarillaSeleccionada, shopifyBundle, fallbackBundle, maskSizing);
    }
    return null;
  }, [isV2, storedBundleInfo, isV1, shouldFetchBundle, equipoSeleccionado, mascarillaSeleccionada, shopifyBundle, fallbackBundle, maskSizing]);

  // Debug logging for troubleshooting
  if (quote && process.env.NODE_ENV === 'development') {
    console.log('🛒 CarritoStep Debug:', {
      quoteMode: isQuoteMode,
      quoteVersion,
      isV1,
      isV2,
      compatibleUserData,
      equipoSeleccionado,
      mascarillaSeleccionada,
      storedBundleInfo,
      bundleInfo,
      shouldFetchBundle,
      shopifyBundle: !!shopifyBundle,
      fallbackBundle: !!fallbackBundle,
      bundleLoading,
      bundleError
    });
  }

  // Use snapshot pricing if available and valid, otherwise use live pricing (version-aware)
  const useSnapshotPricing = isQuoteMode && hasValidSnapshot(quote);
  
  let bundlePrice: number;
  let bunoPrice: number;
  let total: number;
  let pricingSource: 'snapshot' | 'live' | 'stored_kit';
  
  if (useSnapshotPricing && quote?.priceSnapshot) {
    // Use captured pricing from snapshot (backward compatible)
    // Old quotes will show bunoPrice = 2500, new quotes will show bunoPrice = 0
    bundlePrice = quote.priceSnapshot.bundlePrice;
    bunoPrice = quote.priceSnapshot.bunoPrice; // Preserved from original quote
    total = quote.priceSnapshot.totalPrice; // Preserved from original quote
    pricingSource = 'snapshot';

    console.log(`💰 Using snapshot pricing (version ${quoteVersion}):`, {
      bundlePrice: bundlePrice.toLocaleString(),
      bunoPrice: bunoPrice.toLocaleString(),
      total: total.toLocaleString(),
      capturedAt: quote.priceSnapshot.capturedAt,
      version: quoteVersion
    });
  } else {
    // Use live pricing - Buno+ is always included at no cost for new quotes
    bundlePrice = bundleInfo?.price || 0;
    bunoPrice = 0; // Always 0 - Buno+ is complimentary
    total = bundlePrice; // Don't add bunoPrice
    pricingSource = 'live';

    if (isQuoteMode) {
      console.log(`📈 Using live pricing for quote (version ${quoteVersion} - no valid snapshot)`);
    }
  }

  // Update snapshot with bundle info when bundle loads - only in live mode
  useEffect(() => {
    if (bundleInfo && currentSnapshot && !isQuoteMode) {
      updateStage('carrito', {
        id: bundleInfo.id,
        name: bundleInfo.name,
        description: bundleInfo.description,
        price: bundleInfo.price,
        image: bundleInfo.image,
        isShopifyBundle: bundleInfo.isShopifyBundle,
        components: bundleInfo.components,
        bundleMapping: bundleInfo.bundleMapping
      });
    }
  }, [bundleInfo, currentSnapshot, updateStage, isQuoteMode]);

  const procederPago = async () => {
    if (!bundleInfo) return;
    
    // Mark snapshot as used before proceeding to payment (live mode only)
    if (currentSnapshot && !isQuoteMode) {
      try {
        await markAsUsed();
      } catch (error) {
        console.warn('Failed to mark snapshot as used:', error);
      }
    }
    
    // Use the abstracted Shopify checkout logic
    handleShopifyCheckout(
      bundleInfo as BundleInfoForCart,
      maskSizing?.maskSize,
      bunoIncluido,
      quote ? {
        bunoPlusVariantId: quote.bunoPlusVariantId,
        createdAt: quote.createdAt
      } : undefined,
      (error) => {
        console.error('Shopify checkout failed:', error);
        // Fallback to internal payment flow
        navigate('/eleccion/pago');
      }
    );
  };

  const handleShareSnapshot = async () => {
    // In quote mode, share the quote URL directly
    if (isQuoteMode && quote) {
      const { shareQuote } = await import('@/services/quoteService');
      try {
        await shareQuote(quote);
      } catch (error) {
        console.error('Failed to share quote:', error);
      }
      return;
    }
    
    // Live mode: use snapshot sharing
    if (!bundleInfo || !currentSnapshot) return;

    try {
      await shareSnapshotUrl();
    } catch (error) {
      console.error('Failed to share snapshot:', error);
    }
  };

  // Handler to show product details modal
  const handleProductClick = (
    product: any,
    type: 'equipo' | 'mascarilla' | 'plan'
  ) => {
    setSelectedProduct(product);
    setProductModalType(type);
    setProductModalOpen(true);
  };

  // Helper function to render bundle display based on quote version
  const renderBundleDisplay = () => {
    if (!bundleInfo) return null;

    if (isV2 && storedBundleInfo) {
      // Version 2 Quote: Use stored bundle structure with kit branding
      return renderV2BundleDisplay(storedBundleInfo);
    } else {
      // Version 1 Quote or Live Mode: Build kit-style view from individual selections
      return renderV1BundleDisplay(bundleInfo);
    }
  };

  // Version 2 Bundle Display: Uses stored kit data with enhanced component breakdown
  const renderV2BundleDisplay = (bundle: { 
    name: string; 
    image: string; 
    description: string; 
    components?: Array<{ name: string; type: string; imagen?: string }>; 
  }) => {
    return (
      <Card className={`border-primary/20 ${isExpired ? 'bg-gray-50 opacity-75' : 'bg-white'}`}>
        <CardContent className="p-5">
          <div className="flex flex-col gap-4 items-start">
            <div className="w-full">
              <div className="flex flex-row sm:flex-row sm:items-start gap-3">
                <div className="w-16 h-16 sm:w-24 sm:h-24 flex-shrink-0 bg-white rounded-lg overflow-hidden border border-border/40">
                  <img 
                    src={bundle.image} 
                    alt={bundle.name}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full bg-muted/20 flex items-center justify-center"><svg class="h-8 w-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>';
                    }}
                  />
                </div>
                <div className="min-w-0">
                  <div className="flex items-start gap-2 mb-2">
                    <h3 className="font-semibold text-primary text-base sm:text-lg leading-tight flex-1">
                      {bundle.name}
                    </h3>
                  </div>
                  <p 
                    className="text-sm text-muted-foreground mb-3"
                    dangerouslySetInnerHTML={{ __html: bundle.description }}
                  />
                  {bundleError && (
                    <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-xs mb-2">
                      <AlertCircle className="h-3 w-3" />
                      <span>Using stored kit pricing</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* V2 Bundle Components */}
              <div className="space-y-3 mt-4 w-100">
                <p className="text-sm font-semibold text-muted-foreground ml-2">Kit incluye:</p>
                {bundle.components?.map((component: { name: string; type: string }, index: number) => {
                  const isEquipo = component.type?.toLowerCase().includes('equipo') ||
                                   component.type?.toLowerCase().includes('cpap');
                  const isMask = component.type?.toLowerCase().includes('mascarilla') ||
                                component.type?.toLowerCase().includes('mask');

                  // Use component image from bundle data or fallback to compatible data
                  let componentImage = (component as any).imagen || null;
                  if (!componentImage) {
                    if (isEquipo && equipoSeleccionado?.imagen) {
                      componentImage = equipoSeleccionado.imagen;
                    } else if (isMask && mascarillaSeleccionada?.imagen) {
                      componentImage = mascarillaSeleccionada.imagen;
                    }
                  }

                  return (
                    <Card
                      key={index}
                      className="border-muted/30 bg-background/50 cursor-pointer hover:bg-background/80 transition-colors"
                      onClick={() => {
                        if (isEquipo && equipoSeleccionado) {
                          handleProductClick(equipoSeleccionado, 'equipo');
                        } else if (isMask && mascarillaSeleccionada) {
                          handleProductClick(mascarillaSeleccionada, 'mascarilla');
                        }
                      }}
                    >
                      <CardContent className="p-3">
                        <div className="flex gap-3 items-center justify-start">
                          <div className="w-14 h-14 sm:w-12 sm:h-12 flex-shrink-0 bg-white rounded-lg overflow-hidden border border-border/10">
                            {componentImage ? (
                              <img 
                                src={componentImage} 
                                alt={component.name}
                                className="w-full h-full object-contain" 
                              />
                            ) : (
                              <div className="w-full h-full bg-muted/20 flex items-center justify-center">
                                {isEquipo ? (
                                  <Package className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 pr-2">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-sm text-foreground leading-tight">
                                    {component.name}
                                  </h4>
                                  {isMask && maskSizing?.maskSize && (
                                    <Badge variant="outline" className="text-xs hidden sm:inline">
                                      Talla {maskSizing.maskSize}
                                    </Badge>
                                    
                                  )}
                                  {isMask && maskSizing?.maskSize && (
                                    <Badge variant="outline" className="text-xs inline sm:hidden">
                                      {maskSizing.maskSize}
                                    </Badge>
                                  )}
                                  
                                </div>
                                <p 
                                  className="text-sm text-muted-foreground mt-0.5"
                                  dangerouslySetInnerHTML={{ __html: component.type }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Bundle price */}
              <div className="flex-shrink-0 text-right mt-4">
                <p className="font-bold text-md text-primary">${bundlePrice.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Version 1 Bundle Display: Uses computed bundle from individual selections
  const renderV1BundleDisplay = (bundle: { 
    name: string; 
    image: string; 
    description: string; 
    components?: Array<{ name: string; type: string }>; 
  }) => {
    return (
      <Card className={`border-primary/20 ${isExpired ? 'bg-gray-50 opacity-75' : 'bg-white'}`}>
        <CardContent className="p-5">
          <div className="flex flex-col gap-4 items-start">
            <div className="w-full">
              <div className="flex flex-row sm:flex-row sm:items-start gap-3">
                <div className="w-16 h-16 sm:w-24 sm:h-24 flex-shrink-0 bg-white rounded-lg overflow-hidden border border-border/40">
                  <img 
                    src={bundle.image} 
                    alt={bundle.name}
                    className="w-full h-full object-contain"
                    onError={(e) => {
                      e.currentTarget.style.display = 'none';
                      e.currentTarget.parentElement!.innerHTML = '<div class="w-full h-full bg-muted/20 flex items-center justify-center"><svg class="h-8 w-8 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg></div>';
                    }}
                  />
                </div>
                <div className="min-w-0">
                  <div className="flex items-start gap-2 mb-2">
                    <h3 className="font-semibold text-primary text-base sm:text-lg leading-tight flex-1">
                      {bundle.name}
                    </h3>
                    {!isQuoteMode && <Badge variant="outline" className="text-xs opacity-60">Modo compatibilidad</Badge>}
                  </div>
                  <p 
                    className="text-sm text-muted-foreground mb-3"
                    dangerouslySetInnerHTML={{ __html: bundle.description }}
                  />
                  {bundleError && (
                    <div className="flex items-center gap-2 p-2 bg-amber-50 border border-amber-200 rounded text-amber-700 text-xs mb-2">
                      <AlertCircle className="h-3 w-3" />
                      <span>Bundle analysis failed, using standard pricing</span>
                    </div>
                  )}
                </div>
              </div>
              
              {/* V1 Bundle Components */}
              <div className="space-y-3 mt-4 w-100">
                <p className="text-sm font-semibold text-muted-foreground ml-2">Kit incluye:</p>
                {bundle.components?.map((component: { name: string; type: string }, index: number) => {
                  const isEquipo = component.type === 'Equipo CPAP Automático';
                  const isMask = !isEquipo;
                  const componentImage = isEquipo ? equipoSeleccionado?.imagen : mascarillaSeleccionada?.imagen;

                  return (
                    <Card
                      key={index}
                      className="border-muted/30 bg-background/50 cursor-pointer hover:bg-background/80 transition-colors"
                      onClick={() => {
                        if (isEquipo && equipoSeleccionado) {
                          handleProductClick(equipoSeleccionado, 'equipo');
                        } else if (!isEquipo && mascarillaSeleccionada) {
                          handleProductClick(mascarillaSeleccionada, 'mascarilla');
                        }
                      }}
                    >
                      <CardContent className="p-3">
                        <div className="flex gap-3 items-center justify-start">
                          <div className="w-14 h-14 sm:w-12 sm:h-12 flex-shrink-0 bg-white rounded-lg overflow-hidden border border-border/10">
                            {componentImage ? (
                              <img 
                                src={componentImage} 
                                alt={component.name}
                                className="w-full h-full object-contain"
                                onError={(e) => {
                                  e.currentTarget.style.display = 'none';
                                  e.currentTarget.parentElement!.innerHTML = `<div class="w-full h-full bg-muted/20 flex items-center justify-center">${isEquipo ? '<svg class="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M20 7l-8-4-8 4m16 0l-8 4m8-4v10l-8 4m0-10L4 7m8 4v10M4 7v10l8 4"></path></svg>' : '<svg class="h-4 w-4 text-muted-foreground" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M5 13l4 4L19 7"></path></svg>'}</div>`;
                                }}
                              />
                            ) : (
                              <div className="w-full h-full bg-muted/20 flex items-center justify-center">
                                {isEquipo ? (
                                  <Package className="h-4 w-4 text-muted-foreground" />
                                ) : (
                                  <CheckCircle className="h-4 w-4 text-muted-foreground" />
                                )}
                              </div>
                            )}
                          </div>
                          <div className="min-w-0">
                            <div className="flex items-start justify-between gap-2">
                              <div className="min-w-0 pr-2">
                                <div className="flex items-center gap-2">
                                  <h4 className="font-medium text-sm text-foreground leading-tight">
                                    {component.name}
                                  </h4>
                                </div>
                                <p 
                                  className="text-sm text-muted-foreground mt-0.5 inline-block"
                                  dangerouslySetInnerHTML={{ __html: component.type }}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      </CardContent>
                    </Card>
                  );
                })}
              </div>

              {/* Bundle price */}
              <div className="flex-shrink-0 text-right mt-4">
                <p className="font-bold text-md text-primary">${bundlePrice.toLocaleString()}</p>
              </div>
            </div>
          </div>
        </CardContent>
      </Card>
    );
  };

  // Loading state for bundle analysis (version-aware)
  const showLoading = bundleLoading && shouldFetchBundle;
  const loadingMessage = isV2 ? 'Cargando información del kit...' : 'Analizando bundles de Shopify...';
  const loadingSubtext = isV2 ? 'Obteniendo detalles del kit seleccionado' : 'Buscando el mejor paquete para tu selección';

  // Helper function to check if quote timestamp is older than 7 days
  const isOlderThan7Days = (quote: QuoteData): boolean => {
    // Access the underlying timestamp field from the quote object
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const timestamp = (quote as any).timestamp;
    if (!timestamp) return false;
    
    const sevenDaysAgo = new Date(Date.now() - 7 * 24 * 60 * 60 * 1000);
    return new Date(timestamp) < sevenDaysAgo;
  };
  
  if (showLoading) {
    return (
      <div className="flex flex-col min-h-[calc(100vh-80px)]">
        <div className="flex-1 container px-4 py-8 pb-24">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-6 text-center space-y-4">
                <Loader2 className="h-8 w-8 animate-spin text-primary mx-auto" />
                <p>{loadingMessage}</p>
                <p className="text-sm text-muted-foreground">{loadingSubtext}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  if (!bundleInfo) {
    const noDataMessage = isV2 ? 'Kit no disponible' : 'No hay productos seleccionados';
    const noDataSubtext = isV2 ? 'No se pudo cargar la información del kit' : 'Selecciona un equipo y mascarilla';
    
    return (
      <div className="flex flex-col min-h-[calc(100vh-80px)]">
        <div className="flex-1 container px-4 py-8 pb-24">
          <div className="max-w-2xl mx-auto">
            <Card>
              <CardContent className="p-6 text-center space-y-4">
                <AlertCircle className="h-8 w-8 text-muted-foreground mx-auto" />
                <p>{noDataMessage}</p>
                <p className="text-sm text-muted-foreground">{noDataSubtext}</p>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex flex-col min-h-[calc(100vh-80px)]">
      <div className="flex-1">
        <div className="max-w-2xl mx-auto overflow-hidden">
          {/* Pass quote creation date to header */}
          {quote && (
            <div className="fixed top-12 z-50 left-1/2 transform -translate-x-1/2 px-4 text-sm text-muted-foreground flex justify-between w-full max-w-lg">
              <div>
                <b>Creada: </b>{new Date(quote.createdAt).toLocaleDateString('es-MX', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric',
                  timeZone: 'America/Monterrey'
                })}
              </div>

              <div>
                <b>Expira: </b>{new Date(quote.expiresAt).toLocaleDateString('es-MX', { 
                  day: '2-digit', 
                  month: '2-digit', 
                  year: 'numeric',
                  timeZone: 'America/Monterrey'
                })}
              </div>
            </div>
            
          )}
          <CardContent className="p-4">
            <div className="">
              {/* Version-aware Bundle Display */}
              {renderBundleDisplay()}

              {/* Buno+ Program - Always show */}
              <Card
                className={`border-primary/20 mt-4 cursor-pointer hover:bg-gray-50/50 transition-colors ${isExpired ? 'bg-gray-50 opacity-75' : 'bg-white'}`}
                onClick={() => {
                  const bunoPlan = {
                    name: 'Programa de acompañamiento buno+',
                    brand: 'buno',
                    price: bunoPrice,
                    image: 'https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/bunoplus_2.png'
                  };

                  handleProductClick(bunoPlan, 'plan');
                }}
              >
                <CardContent className="p-5 sm:p-4">
                  <div className="flex flex-row gap-3 items-start">
                    <div className="w-16 h-16 sm:w-24 sm:h-24 flex-shrink-0 bg-white rounded-lg overflow-hidden border border-border/10">
                      <img
                        src="https://buno1storage1account.blob.core.windows.net/www-buno/buno-tu-companero-cpap-35/bunoplus_2.png"
                        alt="buno+ programa"
                        className="w-full h-full object-contain"
                      />
                    </div>
                    <div className="flex-1 min-w-0 mb-4">
                      <div className="flex items-center justify-between gap-2">
                        <div>
                          <h3 className="font-medium text-primary text-base">Programa de acompañamiento</h3>
                          <p className="text-sm text-muted-foreground">Acompañamiento integral</p>
                        </div>
                        {bunoPrice === 0 ? (
                          <p className="font-bold text-md text-primary">Incluido</p>
                        ) : (
                          <div className="text-right">
                            <p className="font-bold text-md text-primary">${bunoPrice.toLocaleString()}</p>
                          </div>
                        )}
                      </div>
                    </div>
                  </div>
                </CardContent>
              </Card>
            </div>

            <Separator className="my-2" />

            {/* Order Summary */}
            <Card className={`${isExpired ? 'bg-gray-50 opacity-75' : 'bg-white'}`}>
              <CardContent className="p-4 sm:p-4 space-y-4">
                <div className="flex justify-between gap-2 sm:text-sm">
                  <span className="text-left text-md">{bundleInfo.name}</span>
                  <span className="font-semibold text-md">${bundlePrice.toLocaleString()}</span>
                </div>
                {/* Always show Buno+ in summary - with price for old quotes, "Incluido" for new */}
                <div className="flex justify-between gap-2 sm:text-sm">
                  <span className="text-left text-md">Programa buno+</span>
                  {bunoPrice === 0 ? (
                    <span className="font-semibold text-md">Incluido</span>
                  ) : (
                    <span className="font-semibold text-md">${bunoPrice.toLocaleString()}</span>
                  )}
                </div>
                {/* Value propositions - only show in quote mode */}
                {isQuoteMode && (
                  <>
                    <div className="flex justify-between gap-2 sm:text-sm">
                      <span className="text-left text-md">Garantía de 1 año</span>
                      <span className="font-semibold text-md">Incluída</span>
                    </div>
                    <div className="flex justify-between gap-2 sm:text-sm">
                      <span className="text-left text-md">Envío</span>
                      <span className="font-semibold text-md">Gratis</span>
                    </div>
                  </>
                )}
                <Separator />
                <div className="flex justify-between items-center text-md font-bold">
                  <span>Total:</span>
                  <span className="text-primary font-semibold text-md">${total.toLocaleString()}</span>
                </div>
                {/* Payment info - only show in quote mode */}
                {isQuoteMode && (
                  <div style={{marginTop: "5px"}}>
                    <div className="text-right text-sm text-muted-foreground">
                      Paga hasta en 18 meses con intereses
                    </div>
                    <div className="text-right text-sm text-muted-foreground">
                      IVA incluido
                    </div>
                  </div>
                )}
              </CardContent>
            </Card>

            {/* Pricing Source Notice - Version-aware */}
            {isQuoteMode && pricingSource === 'live' && (
              <div className="mt-3">
                <div className={`p-3 border rounded text-sm ${
                  'bg-blue-50 border-blue-200 text-blue-700'
                }`}>
                  <div className="flex items-start gap-2">
                    <AlertCircle className={`h-4 w-4 mt-0.5 flex-shrink-0 ${
                      'text-blue-600'
                    }`} />
                    <div className="min-w-0">
                      <div>
                        <p className="text-xs font-medium">Precios actuales</p>
                        <p className="mt-1 text-xs">
                          Esta cotización muestra los precios actuales de los productos. 
                          Es posible que los precios hayan cambiado.
                        </p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Show expiration banner for expired quotes */}
            <div className='mt-4'>
              <ExpiredQuoteBanner quote={quote} />
            </div>
            
            {/* Expiration disclaimer - only show in quote mode and not expired */}
            {isQuoteMode && quote && !isExpired && (
              <div className="mt-0 p-2 pt-0 pb-0 text-sm text-muted-foreground text-left">
                * Precios válidos por 7 días desde la entrega de la cotización (Hasta {new Date(quote.expiresAt).toLocaleDateString('es-MX', { 
                day: '2-digit', 
                month: '2-digit', 
                year: 'numeric',
                timeZone: 'America/Monterrey'
              })})
              </div>
            )}

            <div className="mt-4">
              { quote.status !== 'expired' && (
                <Button 
                  className="w-full min-h-touch text-base sm:text-lg px-6 sm:px-8 gap-0" 
                  onClick={procederPago}
                  disabled={snapshotLoading || isExpired}
                >
                  <span className="">
                    {snapshotLoading ? 'Actualizando...' : (isExpired ? 'Cotización expirada' : 'Ir a pagar')}
                  </span>
                  {!snapshotLoading && !isExpired && <ChevronRight style={{ marginLeft: '2.5px', width: '1.25rem', height: '1.25rem', strokeWidth: '3px'}}/>}
                </Button>
              )}

              {/* Contact information - only show in quote mode */}
              {isQuoteMode && quote && (
                <div className="mt-0 mb-4 pt-4 border-t border-border/20 text-center">
                  <p className="text-md text-foreground font-medium mb-1">¿Dudas? Estamos para ayudarte.</p>
                  <p className="text-md text-foreground">
                    <a className="underline" href="tel:+528188687677">81-8868-7677</a> | <a className="underline" href={`https://api.whatsapp.com/send?phone=528188687677&text=Hola%2C%20tengo%20una%20duda%20referente%20a%20mi%20cotizaci%C3%B3n%20https%3A%2F%2Fcotiza.buno.mx%2F${quote.id}`}>WhatsApp</a> | <a className="underline" href='mailto:hola@buno.mx'>hola@buno.mx</a>
                  </p>
                </div>
              )}
              
              
              <div className="border-t border-border mb-4" style={{borderColor: '#102A41'}}></div>

              
              
              {/* Create new quote button - prominent for expired quotes, secondary for others */}
              {isExpired ? (
                <Button 
                  className="w-full min-h-touch text-base sm:text-lg px-6 sm:px-8 gap-0 bg-primary text-muted-foreground hover:bg-primary/90" 
                  onClick={() => navigate('/')}
                >
                  <span>Crear nueva cotización</span>
                  <ChevronRight style={{ marginLeft: '2.5px', width: '1.25rem', height: '1.25rem', strokeWidth: '3px'}}/>
                </Button>
              ) : (currentSnapshot || isQuoteMode) ? (
                <Button 
                  variant="outline"
                  className="w-full min-h-touch text-base text-muted-foreground sm:text-lg px-6 sm:px-8 gap-2" style={{
                    background: 'none',
                    border: 'none',
                    textDecoration: 'underline',
                   }}
                  onClick={() => navigate('/')}
                >
                  <span>Crear nueva cotización</span>
                </Button>
              ) : null}

              {/* Show snapshot error if any */}
              {snapshotError && (
                <p className="text-sm text-red-600 text-center">{snapshotError}</p>
              )}
            </div>
          </CardContent>
        </div>
      </div>

      {/* Product Detail Modal */}
      <ProductInfoModal
        isOpen={productModalOpen}
        onClose={() => setProductModalOpen(false)}
        product={selectedProduct}
        type={productModalType}
      />
    </div>
  );
}